<script setup>
import { ref, shallowRef } from 'vue';
import UiFormDialog from '@/components/ui/UiFormDialog.vue';
import UiTableCard from '@/components/ui/UiTableCard.vue';
import { apiRequest } from '@/api/http';
import { useAsyncAction } from '@/composables/useAsyncAction';
import { useConfirm } from '@/composables/useConfirm';
import { useListResource } from '@/composables/useListResource';
import { useSessionGuard } from '@/composables/useSessionGuard';

useSessionGuard();

// Signatories printed on forms and reports (principal, academic heads,
// division head…). The first two entries sign the school reports, so they
// can be edited but not removed; Form 29's signatories are chosen from this
// list (or typed in) in School Settings.
const headers = [
  { title: 'Name', key: 'name', sortable: false },
  { title: 'Position', key: 'position', sortable: false },
];
const { items, loading, error, load } = useListResource(() =>
  apiRequest('get', '/user-details/getDepEdPersonnel'),
);
load();

const editing = shallowRef(null); // null = adding
const formOpen = shallowRef(false);
const form = ref({ name: '', position: '' });
const fields = [
  { key: 'name', label: 'Name', required: true, placeholder: 'e.g. Gabriel S. Alfonso' },
  { key: 'position', label: 'Position', required: true, placeholder: 'e.g. Principal II' },
];
const { busy, run } = useAsyncAction();
const { confirm } = useConfirm();

function openForm(person = null) {
  editing.value = person;
  form.value = { name: person?.name ?? '', position: person?.position ?? '' };
  formOpen.value = true;
}

async function save() {
  const body = { name: form.value.name.trim(), position: form.value.position.trim() };
  const ok = await run(
    () =>
      editing.value
        ? apiRequest('patch', `/user-details/deped-personnel/${editing.value.id}`, body)
        : apiRequest('post', '/user-details/deped-personnel', body),
    editing.value
      ? { success: 'Personnel updated.', error: 'Could not update the personnel.' }
      : { success: 'Personnel added.', error: 'Could not add the personnel.' },
  );
  if (ok) {
    formOpen.value = false;
    load();
  }
}

async function remove(person) {
  const ok = await confirm({
    title: 'Remove personnel?',
    description: `${person.name} (${person.position}) will no longer be available as a signatory.`,
    confirmText: 'Remove',
    destructive: true,
  });
  if (!ok) return;
  if (
    await run(() => apiRequest('delete', `/user-details/deped-personnel/${person.id}`), {
      success: 'Personnel removed.',
      error: 'Could not remove the personnel.',
    })
  ) {
    load();
  }
}
</script>

<template>
  <UiTableCard
    title="DepEd personnel"
    description="People who sign school forms and reports. Form 29 signatories can be picked from this list in School Settings."
    :headers="headers"
    :items="items"
    :loading="loading"
    :error="error"
    :searchable="false"
    add-label="Add personnel"
    empty-text="No personnel yet."
    row-label="name"
    :row-actions="
      (person) => [
        { title: 'Edit', icon: 'mdi-pencil-outline', onClick: () => openForm(person) },
        { title: 'Remove', icon: 'mdi-trash-can-outline', destructive: true, onClick: () => remove(person) },
      ]
    "
    @add="openForm()"
  >
    <template #[`item.name`]="{ item }">
      <span class="font-weight-medium">{{ item.name }}</span>
    </template>
    <template #[`item.position`]="{ item }">
      <span class="text-medium-emphasis">{{ item.position }}</span>
    </template>
  </UiTableCard>

  <UiFormDialog
    v-model="formOpen"
    :title="editing ? 'Edit personnel' : 'Add personnel'"
    description="Printed as written on forms and reports."
    v-model:values="form"
    :fields="fields"
    :saving="busy"
    :submit-text="editing ? 'Save changes' : 'Add personnel'"
    @submit="save"
  />
</template>
